/**
 * Призрак устанавливаемого предмета: едет за курсором, пока в каталоге выбран тип.
 *
 * Показывает предмет в натуральную величину там, где он встанет по клику: на полу и на
 * потолке — с привязкой к сетке, на стене — прилипшим к ближайшей грани. Вдали от стен
 * прибор «на стену» просто следует за курсором, чтобы было видно, что именно ставим.
 * Рисуется теми же компонентами разделов каталога, что и настоящие предметы, полупрозрачно
 * и мимо указателя: клик под призраком должен дойти до холста или стены.
 */
import type { PlacementGhost } from "@/widgets/place-fixture";
import type { WallGraph } from "../../plan";
import { getCatalogItem } from "@/shared/catalog";
import { CatalogSectionKind } from "@/shared/plan";
import {
	CatalogSymbol,
	CeilingLightShape,
	FurnitureItemShape,
	PlumbingFloorShape,
	symbolRenderSize,
} from "@/widgets/catalog";
import { WallItemShape2D } from "../electrical";

/** Прибор вдали от стены рисуется условным значком такого же размера, как на стене */
const FREE_SCALE = 1.8;
const FREE_MIN_PX = 16;

interface Props {
	ghost: PlacementGhost | null;
	graph: WallGraph;
	mmPerPx: number;
}

export const PlacementGhost2D = ({ ghost, graph, mmPerPx }: Props) => {
	if (!ghost) return null;
	return (
		<g opacity={0.55} style={{ pointerEvents: "none" }}>
			<GhostShape ghost={ghost} graph={graph} mmPerPx={mmPerPx} />
		</g>
	);
};

function GhostShape({ ghost, graph, mmPerPx }: Props & { ghost: PlacementGhost }) {
	switch (ghost.kind) {
		case "wall":
			return (
				<WallItemShape2D graph={graph} fixture={ghost.fixture} mmPerPx={mmPerPx} selected={false} />
			);
		case "ceiling":
			return (
				<g transform={`translate(${ghost.item.x} ${ghost.item.y})`}>
					<CeilingLightShape
						item={ghost.item}
						info={getCatalogItem(ghost.item.ref)}
						selected={false}
						mmPerPx={mmPerPx}
						interactive={false}
					/>
				</g>
			);
		case "floor": {
			// Как выглядит предмет, знает его раздел: сантехника контуром, мебель символом
			const Shape =
				ghost.item.ref.catalog === CatalogSectionKind.Plumbing ||
				ghost.item.ref.catalog === CatalogSectionKind.Water
					? PlumbingFloorShape
					: FurnitureItemShape;
			return (
				<g transform={`translate(${ghost.item.x} ${ghost.item.y})`}>
					<Shape
						item={ghost.item}
						info={getCatalogItem(ghost.item.ref)}
						selected={false}
						mmPerPx={mmPerPx}
						interactive={false}
					/>
				</g>
			);
		}
		case "free": {
			const info = getCatalogItem(ghost.ref);
			if (!info) return null;
			const { width, height } = symbolRenderSize(
				info.width,
				info.height,
				mmPerPx,
				FREE_SCALE,
				FREE_MIN_PX,
			);
			return (
				<g transform={`translate(${ghost.point.x - width / 2} ${ghost.point.y - height / 2})`}>
					<CatalogSymbol
						item={info}
						width={width}
						height={height}
						color={info.color}
						mmPerPx={mmPerPx}
					/>
				</g>
			);
		}
	}
}
